import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const { clearCart } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        setPaymentData(response.data);

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          clearCart();
          return;
        } else if (response.data.status === 'expired') {
          setStatus('failed');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollPaymentStatus, pollInterval);
        } else {
          // Check one more time based on final status
          if (response.data.payment_status === 'paid') {
            setStatus('success');
            clearCart();
          } else {
            setStatus('failed');
          }
        }
      } catch (error) {
        // Error handled silently
        setStatus('failed');
      }
    };

    pollPaymentStatus();
  }, [sessionId, clearCart]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center" data-testid="checkout-success-page">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full mx-4 text-center"
      >
        {status === 'loading' && (
          <div className="space-y-4">
            <Loader2 className="w-16 h-16 mx-auto animate-spin text-muted-foreground" />
            <h1 className="font-heading font-bold text-2xl uppercase">Vérification du paiement...</h1>
            <p className="font-body text-muted-foreground">Veuillez patienter</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <div>
              <h1 className="font-heading font-black text-3xl uppercase">Merci !</h1>
              <p className="font-body text-muted-foreground mt-2">
                Votre paiement a été confirmé. Vous recevrez un email de confirmation sous peu.
              </p>
            </div>
            {paymentData && (
              <div className="bg-secondary p-6 text-left">
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
                  Montant payé
                </p>
                <p className="font-mono text-2xl font-bold">
                  {(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                </p>
              </div>
            )}
            <Link to="/">
              <Button className="h-12 px-8 rounded-none font-mono uppercase tracking-widest" data-testid="return-home">
                <Home className="mr-2 w-4 h-4" />
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        )}

        {status === 'failed' && (
          <div className="space-y-6">
            <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <div>
              <h1 className="font-heading font-black text-3xl uppercase">Échec du paiement</h1>
              <p className="font-body text-muted-foreground mt-2">
                Le paiement n'a pas pu être complété. Veuillez réessayer ou nous contacter.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/checkout">
                <Button className="h-12 px-8 rounded-none font-mono uppercase tracking-widest" data-testid="retry-payment">
                  Réessayer
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="h-12 px-8 rounded-none font-mono uppercase tracking-widest" data-testid="contact-support">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
